// Company number validation patterns by country/market
// Based on official formats from each country's business registry

const companyNumberPatterns = {
	// Austria - Firmenbuchnummer
	AT: {
		name: 'Firmenbuchnummer',
		pattern: /^[A-Z]{2}\s?\d{1,6}[A-Z]?$|^\d{1,6}[A-Z]$/i,
		format: 'FN 123456a or 123456a',
		example: 'FN 123456a',
	},

	// Australia - ABN (Australian Business Number)
	AU: {
		name: 'ABN',
		pattern: /^\d{11}$/,
		format: '11 digits',
		example: '12345678901',
	},

	// Belgium - Enterprise Number
	BE: {
		name: 'Enterprise Number',
		pattern: /^\d{10}$/,
		format: '10 digits',
		example: '0123456789',
	},

	// Canada - Business Number
	CA: {
		name: 'Business Number',
		pattern: /^[A-Z0-9]{5,10}$/i,
		format: '5-10 alphanumeric',
		example: 'BC1234567',
	},

	// Switzerland - UID
	CH: {
		name: 'UID',
		pattern: /^CHE[\s-]?\d{3}[\s.]?\d{3}[\s.]?\d{3}$|^\d{9}$/i,
		format: 'CHE-123.456.789 or 9 digits',
		example: 'CHE-123.456.789',
	},

	// Germany - Handelsregisternummer
	DE: {
		name: 'Handelsregisternummer',
		pattern: /^HR[AB]\s?\d{1,6}\s?[A-Z]?$/i,
		format: 'HRA 12345 or HRB 12345 B',
		example: 'HRB 247469 B',
	},

	// Denmark - CVR (Central Business Register)
	DK: {
		name: 'CVR Number',
		pattern: /^\d{8}$/,
		format: '8 digits',
		example: '12345678',
	},

	// Estonia - Registration Code
	EE: {
		name: 'Registration Code',
		pattern: /^\d{8}$/,
		format: '8 digits',
		example: '12345678',
	},

	// Finland - Y-tunnus (Business ID)
	FI: {
		name: 'Y-tunnus',
		pattern: /^\d{7}-?\d$/,
		format: 'XXXXXXX-X',
		example: '1234567-8',
	},

	// Faroe Islands - V-tal
	FO: {
		name: 'V-tal',
		pattern: /^\d{6}$/,
		format: '6 digits',
		example: '123456',
	},

	// France - SIREN/SIRET
	FR: {
		name: 'SIREN/SIRET',
		pattern: /^\d{9}(\d{5})?$/,
		format: '9 or 14 digits',
		example: '123456789',
	},

	// Ireland - Company Number
	IE: {
		name: 'Company Number',
		pattern: /^\d{5,7}$/,
		format: '5-7 digits',
		example: '123456',
	},

	// Lithuania - Company Code
	LT: {
		name: 'Company Code',
		pattern: /^\d{9}$/,
		format: '9 digits',
		example: '123456789',
	},

	// Luxembourg - RCS Number
	LU: {
		name: 'RCS Number',
		pattern: /^[A-Z]\d{3,6}$/i,
		format: 'B123456',
		example: 'B123456',
	},

	// Latvia - Registration Number
	LV: {
		name: 'Registration Number',
		pattern: /^\d{11}$/,
		format: '11 digits',
		example: '12345678901',
	},

	// Netherlands - KvK Number
	NL: {
		name: 'KvK Number',
		pattern: /^\d{8}$/,
		format: '8 digits',
		example: '12345678',
	},

	// Norway - Organization Number
	NO: {
		name: 'Organization Number',
		pattern: /^\d{9}$/,
		format: '9 digits',
		example: '123456789',
	},

	// New Zealand - NZBN
	NZ: {
		name: 'NZBN',
		pattern: /^\d{13}$/,
		format: '13 digits',
		example: '1234567890123',
	},

	// Sweden - Organization Number
	SE: {
		name: 'Organization Number',
		pattern: /^\d{6}-?\d{4}$/,
		format: 'XXXXXX-XXXX',
		example: '123456-7890',
	},

	// United Kingdom - Company Number
	UK: {
		name: 'Company Number',
		pattern: /^([A-Z]{2})?\d{6,8}$/i,
		format: '8 digits or 2 letters + 6 digits',
		example: '12345678',
	},
}

/**
 * Validates a company number for a specific country
 * @param {string} companyNumber - The company number to validate
 * @param {string} countryCode - ISO country code (e.g., 'DK', 'SE')
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateCompanyNumber = (companyNumber, countryCode) => {
	if (!companyNumber || !countryCode) {
		return true // Allow empty values (not required field)
	}

	const trimmedNumber = companyNumber.trim()
	if (trimmedNumber === '') {
		return true
	}

	const pattern = companyNumberPatterns[countryCode]
	if (!pattern) {
		// No validation pattern for this country - accept any value
		return true
	}

	return pattern.pattern.test(trimmedNumber)
}

/**
 * Gets the company number field label for a specific country
 * @param {string} countryCode - ISO country code
 * @returns {string} - Field label
 */
export const getCompanyNumberLabel = (countryCode) => {
	const pattern = companyNumberPatterns[countryCode]
	return pattern ? pattern.name : 'Company Number'
}

/**
 * Gets the placeholder text for a specific country
 * @param {string} countryCode - ISO country code
 * @returns {string} - Placeholder text
 */
export const getCompanyNumberPlaceholder = (countryCode) => {
	const pattern = companyNumberPatterns[countryCode]
	if (!pattern) {
		return 'Enter company number'
	}
	return `${pattern.format} (e.g., ${pattern.example})`
}

/**
 * Gets validation error message for a specific country
 * @param {string} countryCode - ISO country code
 * @returns {string} - Error message
 */
export const getCompanyNumberError = (countryCode) => {
	const pattern = companyNumberPatterns[countryCode]
	if (!pattern) {
		return 'Invalid company number'
	}
	return `Invalid ${pattern.name}. Expected format: ${pattern.format}`
}

export { companyNumberPatterns }
